import React, { useContext } from "react";

import FieldsetGroup from "@/Common/Components/Inputs/FieldsetGroup/FieldsetGroup";
import { InlineOptionsListControl } from "@/Common/Components/Inputs/InlineOptionsControl";
import { useTranslation } from "@/Common/CustomHooks/useTranslation";
import { DateTimeWithPrecision } from "@/Common/Models/DateTimeWithPrecision";
import { TextInput } from "@/DesignSystem";
import { CheckboxLabel } from "@/DesignSystem/Molecules/Checkbox";
import { EncounterDateContext } from "@/Flowsheets/Contexts/EncounterDateContext";
import { FirstLineReviewedCareElement } from "@/Flowsheets/Models/CareElements/FirstLineReviewedCareElement";
import { CareElementDateReviewedRow } from "./CareElementDateReviewedRow";

import styles from "./CareElementFirstLineDateReviewed.module.scss";

type OnChange = (element: FirstLineReviewedCareElement) => void;

/** Props for CareElementFirstLineDateReviewed. */
interface CareElementFirstLineDateReviewedProps {
    /** Section title shown on the left side of the row. */
    title: string;
    /** Whether inputs are enabled. When false, all controls are read-only. */
    isEditable: boolean;
    /** Composite first-line care element holding booleans and agent/dose fields. */
    element: FirstLineReviewedCareElement;
    /** Unified change handler. Receives the updated care element to replace in state. */
    onChange: OnChange;
}

/**
 * Renders a First-Line medication row with optional intolerant/contraindicated/target-reached radios
 * and Agent/Dose text inputs. Emits whole-element updates via onChange for upstream state replacement.
 */
export const CareElementFirstLineDateReviewed = React.memo(
    (props: Readonly<CareElementFirstLineDateReviewedProps>): JSX.Element => {
        const { isEditable, element, onChange } = props;
        const { getText } = useTranslation();
        const { encounterDateTimeWithPrecision } =
            useContext(EncounterDateContext);

        function hasText(value?: string): boolean {
            return (value ?? "").trim().length > 0;
        }

        function hasContent(careElement: FirstLineReviewedCareElement): boolean {
            return (
                careElement.IsIntolerant === true ||
                careElement.IsContraindicated === true ||
                (careElement.IsTargetReached !== null && careElement.IsTargetReached !== undefined) ||
                hasText(careElement.Agent) ||
                hasText(careElement.Dose)
            );
        }

        function computeDateReviewed(updatedElement: FirstLineReviewedCareElement): DateTimeWithPrecision {
            if (!hasContent(updatedElement)) {
                return null;
            }

            const hasExistingDateValue = Boolean(element.DateReviewedWithPrecision?.value);
            return hasExistingDateValue ? element.DateReviewedWithPrecision : encounterDateTimeWithPrecision;
        }

        return (
            <CareElementDateReviewedRow
                label={props.title}
                isEditable={isEditable}
                element={element}
                onChange={onChange}
                side="column"
                isDatePickerVisible={hasContent(element)}
            >
                <div className={styles.inputsContainer}>
                    <CheckboxLabel
                        id={`checkbox-${element.Name}-intolerant`}
                        dataCy={`${element.Name}-intolerant`}
                        label={getText(
                            "Flowsheets_HeartFailureBasicCareComponentLabel_Intolerant"
                        )}
                        checked={!!element.IsIntolerant}
                        disabled={!isEditable}
                        onChange={value => {
                            const updatedElement = { ...element, IsIntolerant: value };
                            onChange({ ...updatedElement, DateReviewedWithPrecision: computeDateReviewed(updatedElement) });
                        }}
                    />
                    <CheckboxLabel
                        id={`checkbox-${element.Name}-contraindicated`}
                        label={getText(
                            "Flowsheets_HeartFailureBasicCareComponentLabel_Contraindicated"
                        )}
                        checked={!!element.IsContraindicated}
                        disabled={!isEditable}
                        onChange={value => {
                            const updatedElement = { ...element, IsContraindicated: value };
                            onChange({ ...updatedElement, DateReviewedWithPrecision: computeDateReviewed(updatedElement) });
                        }}
                    />
                </div>
                <FieldsetGroup
                    legend={getText(
                        "Flowsheets_HeartFailureBasicCareComponentLabel_TargetDoseReached"
                    )}
                    legendClassName={styles.fieldsetLegend}
                    fieldSetClassName={styles.radiobuttonsFieldsetBodyContainer}
                >
                    <InlineOptionsListControl.YesOrNo
                        id={`${element.Name}-target-reached`}
                        value={element.IsTargetReached ?? null}
                        onChange={option => {
                            let yesOrNoValue: boolean | null = null;
                            if (element?.IsTargetReached !== option.Key) {
                                yesOrNoValue = option.Key;
                            }
                            const updatedElement = { ...element, IsTargetReached: yesOrNoValue };
                            onChange({ ...updatedElement, DateReviewedWithPrecision: computeDateReviewed(updatedElement) });
                        }}
                        disabled={!isEditable}
                        optionSpacing="24px"
                    />
                </FieldsetGroup>
                <div className={styles.inputsContainer}>
                    <TextInput
                        id={`${element.Name}-agent`}
                        label={getText(
                            "Flowsheets_HeartFailureBasicCareComponentLabel_Agent"
                        )}
                        labelPosition="top"
                        value={element.Agent ?? ""}
                        onChange={e => {
                            const newValue = e.target.value ?? "";
                            const updatedElement = { ...element, Agent: newValue };
                            onChange({ ...updatedElement, DateReviewedWithPrecision: computeDateReviewed(updatedElement) });
                        }}
                        disabled={!isEditable}
                        maxLength={200}
                    />
                    <TextInput
                        id={`${element.Name}-dose`}
                        label={getText(
                            "Flowsheets_HeartFailureBasicCareComponentLabel_Dose"
                        )}
                        labelPosition="top"
                        value={element.Dose ?? ""}
                        onChange={e => {
                            const newValue = e.target.value ?? "";
                            const updatedElement = { ...element, Dose: newValue };
                            onChange({ ...updatedElement, DateReviewedWithPrecision: computeDateReviewed(updatedElement) });
                        }}
                        disabled={!isEditable}
                        maxLength={200}
                    />
                </div>
            </CareElementDateReviewedRow>
        );
    }
);
